/**
 * F-508-FE-02: AdminDashboardPage
 *
 * Top-level admin dashboard at /admin/dashboard.
 * - 4 metric cards: Total Hospitals, Active Doctors, Active Sessions (30s auto-refresh), Revenue This Month
 * - Hospital table: name, status, doctors, sessions, minutes, billing
 * - Per-row actions: View, Edit Settings (navigates to Settings tab), Deactivate (confirmation dialog), Reactivate (for inactive hospitals)
 * - Create New Hospital button
 * - Empty state with prominent CTA
 *
 * Design: Bloomberg Terminal meets modern design system.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AdminLayout } from '@/layouts/AdminLayout';
import { api } from '@/lib/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface HospitalRow {
  id: string;
  name: string;
  status: 'ACTIVE' | 'INACTIVE';
  activeDoctors: number;
  licensedDoctors: number;
  activeSessions: number;
  monthlyMinutes: number;
  billingAmount: number;
  currency: string;
}

interface DashboardMetrics {
  totalHospitals: number;
  activeDoctors: number;
  activeSessions: number;
  revenueThisMonth: number;
  currency: string;
  hospitals: HospitalRow[];
}

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------

function BuildingIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" />
    </svg>
  );
}

function UsersIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
    </svg>
  );
}

function ActivityIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.875 1.875 0 010 3.75H5.625a1.875 1.875 0 010-3.75z" />
    </svg>
  );
}

function CurrencyIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Status badge
// ---------------------------------------------------------------------------

function StatusBadge({ status }: { status: 'ACTIVE' | 'INACTIVE' }) {
  if (status === 'ACTIVE') {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-teal-50 text-teal-700 border border-teal-200">
        Active
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600 border border-gray-200">
      Inactive
    </span>
  );
}

// ---------------------------------------------------------------------------
// Metric card
// ---------------------------------------------------------------------------

interface MetricCardProps {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  refreshing?: boolean;
  accent?: 'teal' | 'amber' | 'charcoal' | 'green';
}

function MetricCard({ label, value, icon, refreshing = false, accent = 'teal' }: MetricCardProps) {
  const accentMap = {
    teal: 'text-clinical-teal',
    amber: 'text-clinical-amber',
    charcoal: 'text-clinical-charcoal',
    green: 'text-green-600',
  };

  return (
    <div className="clinical-card p-6 flex items-start gap-4">
      <div className={`flex-none p-2.5 rounded-xl bg-clinical-cream-dark ${accentMap[accent]}`}>
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-body font-semibold text-clinical-text-muted uppercase tracking-widest mb-1">
          {label}
        </p>
        <div className="flex items-baseline gap-2">
          <p className="text-2xl font-heading font-bold text-clinical-charcoal leading-none">
            {value}
          </p>
          {refreshing && (
            <div className="w-3 h-3 border border-clinical-teal border-t-transparent rounded-full animate-spin flex-none" />
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Confirmation dialog
// ---------------------------------------------------------------------------

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description: string;
  confirmLabel?: string;
  confirmClassName?: string;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
}

function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = 'Confirm',
  confirmClassName,
  onConfirm,
  onCancel,
  isLoading = false,
}: ConfirmDialogProps) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-clinical-charcoal/50 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl shadow-clinical-lg max-w-md w-full p-6">
        <h3 className="font-heading text-lg font-semibold text-clinical-charcoal mb-2">{title}</h3>
        <p className="text-sm font-body text-clinical-text-muted mb-6">{description}</p>
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="btn-secondary px-4 py-2 text-sm min-h-[36px]"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isLoading}
            className={confirmClassName ?? 'inline-flex items-center justify-center gap-2 px-4 py-2 text-sm min-h-[36px] bg-red-600 text-white font-body font-medium rounded-clinical hover:bg-red-700 transition-colors disabled:opacity-50'}
          >
            {isLoading ? 'Processing…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create Hospital Modal
// ---------------------------------------------------------------------------

interface CreateHospitalModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

function CreateHospitalModal({ open, onClose, onCreated }: CreateHospitalModalProps) {
  const [name, setName] = useState('');
  const [maxDoctors, setMaxDoctors] = useState('10');
  const [billingTier, setBillingTier] = useState('FREE');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    setIsSubmitting(true);
    setError('');

    try {
      await api.post('/admin/hospitals', {
        name: name.trim(),
        maxDoctorCount: parseInt(maxDoctors, 10),
        billingTier,
      });
      onCreated();
      onClose();
      setName('');
      setMaxDoctors('10');
      setBillingTier('FREE');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create hospital.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-clinical-charcoal/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-clinical-lg max-w-md w-full p-6">
        <h3 className="font-heading text-lg font-semibold text-clinical-charcoal mb-5">
          Create New Hospital
        </h3>
        <form onSubmit={(e) => { void handleSubmit(e); }} className="space-y-4">
          <div>
            <label htmlFor="hospital-name" className="form-label">Hospital Name</label>
            <input
              id="hospital-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="form-input"
              placeholder="e.g. King Faisal Medical Center"
              required
              autoFocus
            />
          </div>
          <div>
            <label htmlFor="max-doctors" className="form-label">Max Doctor Count</label>
            <input
              id="max-doctors"
              type="number"
              min="1"
              max="500"
              value={maxDoctors}
              onChange={(e) => setMaxDoctors(e.target.value)}
              className="form-input"
              required
            />
          </div>
          <div>
            <label htmlFor="billing-tier" className="form-label">Billing Tier</label>
            <select
              id="billing-tier"
              value={billingTier}
              onChange={(e) => setBillingTier(e.target.value)}
              className="form-input"
            >
              <option value="FREE">Free</option>
              <option value="BASIC">Basic</option>
              <option value="PROFESSIONAL">Professional</option>
              <option value="ENTERPRISE">Enterprise</option>
            </select>
          </div>
          {error && (
            <div className="rounded-clinical border border-red-200 bg-red-50 px-4 py-3">
              <p className="text-sm font-body text-red-700">{error}</p>
            </div>
          )}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary px-4 py-2 text-sm min-h-[36px]">
              Cancel
            </button>
            <button type="submit" disabled={isSubmitting || !name.trim()} className="btn-primary px-4 py-2 text-sm min-h-[36px]">
              {isSubmitting ? 'Creating…' : 'Create Hospital'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Skeleton loaders
// ---------------------------------------------------------------------------

function MetricCardSkeleton() {
  return (
    <div className="clinical-card p-6 flex items-start gap-4">
      <div className="skeleton w-10 h-10 rounded-xl flex-none" />
      <div className="flex-1 space-y-2">
        <div className="skeleton h-3 w-24 rounded" />
        <div className="skeleton h-7 w-16 rounded" />
      </div>
    </div>
  );
}

function TableSkeleton() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="skeleton h-12 w-full rounded-clinical" />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export function AdminDashboardPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [deactivateTarget, setDeactivateTarget] = useState<HospitalRow | null>(null);
  const [reactivateTarget, setReactivateTarget] = useState<HospitalRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<HospitalRow | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Polling interval ref for active sessions refresh
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const { data: metrics, isLoading, isRefetching, refetch } = useQuery<DashboardMetrics>({
    queryKey: ['admin', 'dashboard'],
    queryFn: () => api.get<DashboardMetrics>('/admin/dashboard'),
    staleTime: 25_000,
    refetchInterval: 30_000, // 30s auto-refresh for active sessions
  });

  // Manual 30s polling for active sessions count (belt + suspenders approach)
  useEffect(() => {
    pollRef.current = setInterval(() => { void refetch(); }, 30_000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [refetch]);

  const deactivateMutation = useMutation({
    mutationFn: (hospitalId: string) =>
      api.post<void>(`/admin/hospitals/${hospitalId}/deactivate`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'dashboard'] });
      setDeactivateTarget(null);
    },
  });

  const reactivateMutation = useMutation({
    mutationFn: (hospitalId: string) =>
      api.post<void>(`/admin/hospitals/${hospitalId}/activate`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'dashboard'] });
      setReactivateTarget(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (hospitalId: string) =>
      api.delete<void>(`/admin/hospitals/${hospitalId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'dashboard'] });
      setDeleteTarget(null);
    },
  });

  const handleDeactivate = useCallback(() => {
    if (deactivateTarget) {
      deactivateMutation.mutate(deactivateTarget.id);
    }
  }, [deactivateTarget, deactivateMutation]);

  const handleReactivate = useCallback(() => {
    if (reactivateTarget) {
      reactivateMutation.mutate(reactivateTarget.id);
    }
  }, [reactivateTarget, reactivateMutation]);

  const handleDelete = useCallback(() => {
    if (deleteTarget) {
      deleteMutation.mutate(deleteTarget.id);
    }
  }, [deleteTarget, deleteMutation]);

  const formatCurrency = (amount: number, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatMinutes = (mins: number) => {
    if (mins >= 10_000) return `${Math.round(mins / 60)}h`;
    return `${mins.toLocaleString()}m`;
  };

  const hospitals = metrics?.hospitals ?? [];

  return (
    <AdminLayout>
      {/* Page header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-2xl font-bold text-clinical-charcoal">
            Dashboard
          </h1>
          <p className="text-sm font-body text-clinical-text-muted mt-1">
            Platform overview — hospitals, doctors, sessions, and revenue.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowCreateModal(true)}
          className="btn-primary text-sm px-4 py-2 min-h-[40px]"
        >
          <PlusIcon />
          New Hospital
        </button>
      </div>

      {/* Metric cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
        {isLoading ? (
          Array.from({ length: 4 }).map((_, i) => <MetricCardSkeleton key={i} />)
        ) : (
          <>
            <MetricCard
              label="Total Hospitals"
              value={metrics?.totalHospitals ?? 0}
              icon={<BuildingIcon />}
              accent="charcoal"
            />
            <MetricCard
              label="Active Doctors"
              value={metrics?.activeDoctors ?? 0}
              icon={<UsersIcon />}
              accent="teal"
            />
            <MetricCard
              label="Active Sessions"
              value={metrics?.activeSessions ?? 0}
              icon={<ActivityIcon />}
              accent="green"
              refreshing={isRefetching}
            />
            <MetricCard
              label="Revenue This Month"
              value={formatCurrency(metrics?.revenueThisMonth ?? 0, metrics?.currency)}
              icon={<CurrencyIcon />}
              accent="amber"
            />
          </>
        )}
      </div>

      {/* Hospital table */}
      <div className="clinical-card overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-clinical-border">
          <h2 className="font-heading text-base font-semibold text-clinical-charcoal">
            Hospitals
          </h2>
          <span className="text-sm font-body text-clinical-text-muted">
            {hospitals.length} {hospitals.length === 1 ? 'hospital' : 'hospitals'}
          </span>
        </div>

        {isLoading ? (
          <div className="p-6">
            <TableSkeleton />
          </div>
        ) : hospitals.length === 0 ? (
          // Empty state
          <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
            <div className="w-16 h-16 rounded-full bg-clinical-cream-dark flex items-center justify-center mb-4">
              <BuildingIcon />
            </div>
            <h3 className="font-heading text-lg font-semibold text-clinical-charcoal mb-2">
              No hospitals yet
            </h3>
            <p className="text-sm font-body text-clinical-text-muted max-w-xs mb-6">
              Create your first hospital to start onboarding doctors and launching sessions.
            </p>
            <button
              type="button"
              onClick={() => setShowCreateModal(true)}
              className="btn-primary text-sm px-4 py-2 min-h-[40px]"
            >
              <PlusIcon />
              Create First Hospital
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm font-body">
              <thead>
                <tr className="border-b border-clinical-border bg-clinical-cream">
                  <th className="text-start px-6 py-3 text-xs font-semibold text-clinical-text-muted uppercase tracking-wide">
                    Hospital
                  </th>
                  <th className="text-start px-4 py-3 text-xs font-semibold text-clinical-text-muted uppercase tracking-wide">
                    Status
                  </th>
                  <th className="text-end px-4 py-3 text-xs font-semibold text-clinical-text-muted uppercase tracking-wide">
                    Doctors
                  </th>
                  <th className="text-end px-4 py-3 text-xs font-semibold text-clinical-text-muted uppercase tracking-wide">
                    Active Sessions
                  </th>
                  <th className="text-end px-4 py-3 text-xs font-semibold text-clinical-text-muted uppercase tracking-wide">
                    Monthly Minutes
                  </th>
                  <th className="text-end px-4 py-3 text-xs font-semibold text-clinical-text-muted uppercase tracking-wide">
                    Billing
                  </th>
                  <th className="text-end px-6 py-3 text-xs font-semibold text-clinical-text-muted uppercase tracking-wide">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-clinical-border">
                {hospitals.map((hospital, idx) => (
                  <tr
                    key={hospital.id}
                    className={`hover:bg-clinical-cream/50 transition-colors ${idx % 2 === 0 ? '' : 'bg-clinical-cream/30'}`}
                  >
                    <td className="px-6 py-4">
                      <button
                        type="button"
                        onClick={() => navigate(`/admin/hospitals/${hospital.id}`)}
                        className="font-semibold text-clinical-charcoal hover:text-clinical-teal transition-colors text-start"
                      >
                        {hospital.name}
                      </button>
                    </td>
                    <td className="px-4 py-4">
                      <StatusBadge status={hospital.status} />
                    </td>
                    <td className="px-4 py-4 text-end text-clinical-charcoal">
                      <span className="font-semibold">{hospital.activeDoctors}</span>
                      <span className="text-clinical-text-muted">/{hospital.licensedDoctors}</span>
                    </td>
                    <td className="px-4 py-4 text-end">
                      {hospital.activeSessions > 0 ? (
                        <span className="font-semibold text-green-600">{hospital.activeSessions}</span>
                      ) : (
                        <span className="text-clinical-text-muted">0</span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-end text-clinical-charcoal">
                      {formatMinutes(hospital.monthlyMinutes)}
                    </td>
                    <td className="px-4 py-4 text-end text-clinical-charcoal font-semibold">
                      {formatCurrency(hospital.billingAmount, hospital.currency)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => navigate(`/admin/hospitals/${hospital.id}`)}
                          className="text-xs font-body font-medium text-clinical-teal hover:underline"
                        >
                          View
                        </button>
                        <span className="text-clinical-border">|</span>
                        <button
                          type="button"
                          onClick={() => navigate(`/admin/hospitals/${hospital.id}?tab=settings`)}
                          className="text-xs font-body font-medium text-clinical-text-muted hover:text-clinical-charcoal"
                        >
                          Settings
                        </button>
                        {hospital.status === 'ACTIVE' && (
                          <>
                            <span className="text-clinical-border">|</span>
                            <button
                              type="button"
                              onClick={() => setDeactivateTarget(hospital)}
                              className="text-xs font-body font-medium text-red-500 hover:text-red-700"
                            >
                              Deactivate
                            </button>
                          </>
                        )}
                        {hospital.status !== 'ACTIVE' && (
                          <>
                            <span className="text-clinical-border">|</span>
                            <button
                              type="button"
                              onClick={() => setReactivateTarget(hospital)}
                              className="text-xs font-body font-medium text-teal-600 hover:text-teal-800"
                            >
                              Reactivate
                            </button>
                            <span className="text-clinical-border">|</span>
                            <button
                              type="button"
                              onClick={() => setDeleteTarget(hospital)}
                              className="text-xs font-body font-medium text-red-600 hover:text-red-800"
                            >
                              Delete
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Deactivate confirmation */}
      <ConfirmDialog
        open={deactivateTarget !== null}
        title={`Deactivate ${deactivateTarget?.name ?? ''}?`}
        description="Deactivating this hospital will prevent doctors from starting new sessions. Existing sessions will be terminated. This action can be reversed by reactivating the hospital."
        confirmLabel="Deactivate Hospital"
        onConfirm={handleDeactivate}
        onCancel={() => setDeactivateTarget(null)}
        isLoading={deactivateMutation.isPending}
      />

      {/* Reactivate confirmation */}
      <ConfirmDialog
        open={reactivateTarget !== null}
        title={`Reactivate ${reactivateTarget?.name ?? ''}?`}
        description="Reactivating this hospital will allow doctors to log in and start new sessions immediately."
        confirmLabel="Reactivate Hospital"
        confirmClassName="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm min-h-[36px] bg-clinical-teal text-white font-body font-medium rounded-clinical hover:bg-clinical-teal/90 transition-colors disabled:opacity-50"
        onConfirm={handleReactivate}
        onCancel={() => setReactivateTarget(null)}
        isLoading={reactivateMutation.isPending}
      />

      {/* Delete hospital confirmation */}
      <ConfirmDialog
        open={deleteTarget !== null}
        title={`Permanently Delete ${deleteTarget?.name ?? ''}?`}
        description={`This will permanently remove this hospital and all its doctors from the platform. All doctor accounts will be deleted and their sessions revoked. This action cannot be undone.`}
        confirmLabel="Delete Hospital Permanently"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
        isLoading={deleteMutation.isPending}
      />

      {/* Create hospital modal */}
      <CreateHospitalModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={() => void queryClient.invalidateQueries({ queryKey: ['admin', 'dashboard'] })}
      />
    </AdminLayout>
  );
}
